import { useEffect, useState } from 'react';
import ReactConfetti from 'react-confetti';
import { useWindowDimensions } from '../hooks/useWindowDimensions';
import { apiClient } from '../api';
import { useTenant } from '../FeatureFlags';
import { ConfirmationCode } from '../components/ConfirmationCode';
import { useInitialParamsSnapshot } from '../hooks/useInitialParams';
import { SURVEY_QUERY_PARAM_KROGER } from '../constants/surveyQueryParams';

type SurveyMeta = {
  description: string;
  questionCount: number;
  estimatedMinutes: number;
};

type SuccessPageProps = {
  isExternalCompletionUser?: boolean;
  tapResearchRedirectUrl?: string | null;
  showPostConnectSurvey?: boolean;
  surveyId?: string | null;
  sessionId?: string | null;
  retailerName?: string;
  onContinue?: () => void;
};

export function SuccessPage({
  isExternalCompletionUser = false,
  tapResearchRedirectUrl,
  showPostConnectSurvey = false,
  surveyId,
  sessionId,
  retailerName = 'Amazon',
  onContinue,
}: SuccessPageProps) {
  const tenant = useTenant();
  const { survey, tid } = useInitialParamsSnapshot();
  const isKroger = survey === SURVEY_QUERY_PARAM_KROGER;
  const [showConfetti, setShowConfetti] = useState(true);
  const [surveyMeta, setSurveyMeta] = useState<SurveyMeta | null>(null);
  const windowDimensions = useWindowDimensions(showConfetti);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showPostConnectSurvey || !surveyId || isKroger) return;
    let active = true;
    apiClient
      .getPostConnectSurveyQuestions(surveyId)
      .then((data) => {
        if (!active) return;
        setSurveyMeta({
          description:
            data.description ??
            'A few quick questions help us make sense of your purchases.',
          questionCount: data.questions.length,
          estimatedMinutes: data.estimatedMinutes ?? 1,
        });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [showPostConnectSurvey, surveyId, isKroger]);

  useEffect(() => {
    if (!isExternalCompletionUser || showPostConnectSurvey) return;

    if (tid) {
      if (tapResearchRedirectUrl) window.location.href = tapResearchRedirectUrl;
      return;
    }

    let isMounted = true;
    void (async () => {
      const { redirectUrl } = await apiClient.getQuestionProRedirectUrl();
      if (isMounted && redirectUrl) {
        window.location.href = redirectUrl;
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    isExternalCompletionUser,
    showPostConnectSurvey,
    tid,
    tapResearchRedirectUrl,
  ]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center py-12 px-6">
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={300}
          gravity={0.15}
        />
      )}
      <div className="flex flex-col items-center text-center mt-4">
        {/* Success ring */}
        <div className="success-ring mb-7">
          <svg
            className="w-9 h-9"
            style={{ color: 'var(--retailer-success)' }}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        {showPostConnectSurvey ? (
          <>
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary mb-3">
              {retailerName} connected
            </p>
            <h1 className="font-display text-[clamp(24px,3vw,32px)] font-bold text-ink tracking-[-0.02em] mb-3">
              {isKroger
                ? 'Thanks for sharing your Amazon purchase history!'
                : 'One last thing'}
            </h1>
            <p
              className={
                isKroger
                  ? 'text-[15px] text-ink-muted leading-[1.6] mb-8 max-w-[440px] text-center'
                  : 'text-[16px] text-ink-muted leading-[1.65] mb-5 max-w-[380px]'
              }
            >
              {isKroger
                ? "All that's left is to upload a screenshot or photo of your Kroger loyalty card that clearly shows your membership number. We'll use this only to verify that you're a Kroger shopper, which helps us process your reward more quickly."
                : (surveyMeta?.description ??
                  'A few quick questions help us make sense of your purchases.')}
            </p>
            {!isKroger && (
              <div className="inline-flex items-center rounded-full bg-surface border border-rule px-4 py-2 text-sm font-medium text-ink-muted mb-8">
                {surveyMeta
                  ? `${surveyMeta.questionCount} questions · ${surveyMeta.estimatedMinutes} minute`
                  : '…'}
              </div>
            )}
            <button
              type="button"
              data-testid="success-continue-button"
              onClick={onContinue}
              className="w-full max-w-[320px] min-h-[52px] rounded-xl bg-primary text-white font-semibold shadow-sm hover:bg-primary-deep transition-colors"
            >
              {isKroger ? 'Upload an image' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary mb-3">
              {retailerName} connected
            </p>
            <h1 className="font-display text-[clamp(24px,3vw,32px)] font-bold text-ink tracking-[-0.02em] mb-3">
              You're all set!
            </h1>
            <p className="text-[16px] text-ink-muted leading-[1.65] mb-2 max-w-[380px]">
              {tenant.id === 'verify'
                ? `Your ${retailerName} account is linked, and you're officially part of the Verified Buyer Panel. More exclusive, high-value studies are now unlocked!`
                : `Your ${retailerName} account is linked and your e-receipts are connected.`}
            </p>

            {tenant.flow.showConfirmationCode && sessionId && (
              <ConfirmationCode
                label={tenant.copy.confirmationCodeLabel}
                code={sessionId.slice(-5)}
                hint={tenant.copy.confirmationCodeHint}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
